import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { useAppDispatch, useAppSelector } from "app/hooks";
import useVisibleMenuItems from "features/app-layout/layout/layout-side-menu/use-visible-menu-items.hook";
import useSwitchPractice from "features/app-layout/layout/layout-top-bar/practice-selector/use-switch-practice.hook";
import useActiveSessionContext from "features/app-layout/layout/practice-context-banner/use-active-session-context.hook";
import {
  logoutUser,
  selectCurrentPractice,
  selectCurrentUser,
  selectCurrentUserToken
} from "features/auth/auth-slice";
import { QUICK_LAUNCHER_FOCUS_PATIENT_SEARCH } from "hooks/use-event-bus/event-bus";
import { useEventBusEmit } from "hooks/use-event-bus/use-event-bus-emit.hook";
import useUserRoles from "hooks/use-user-roles.hook";

import {
  openQuickLauncherHelp,
  requestNewAppointment,
  requestScreenshot,
  selectHiddenCallsChipShown,
  selectPracticeBannerHidden,
  toggleHiddenCallsChipShown,
  togglePracticeBannerHidden
} from "./quick-launcher.slice";
import type { QuickLauncherAction } from "./quick-launcher.types";

type QuickLauncherActionsOptions = {
  // "Search patients" focuses the top bar's search box, which only the main
  // app shell has. Administration has its own top bar without one.
  hasPatientSearch: boolean;
};

/**
 * Every quick-launcher entry, in section order.
 *
 * The "Navigate" section is derived from `useVisibleMenuItems`, the same
 * entitlement filter the side menu uses, so the launcher can never offer a
 * destination that would bounce the user off a protected route. The two
 * context sections are passed in by the shell: the main app builds them with
 * `useQuickLauncherContextActions`, and Administration has none.
 *
 * This is also the one place `sunoOnly` is enforced: anything flagged is
 * dropped for users outside the Suno group before the list leaves the hook,
 * so the component and the filter never see an entry the user may not run.
 */
export default function useQuickLauncherActions(
  contextActions: QuickLauncherAction[],
  { hasPatientSearch }: QuickLauncherActionsOptions
): QuickLauncherAction[] {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isSunoAdmin } = useUserRoles();
  const currentUser = useAppSelector(selectCurrentUser);
  const token = useAppSelector(selectCurrentUserToken);
  const practiceBannerHidden = useAppSelector(selectPracticeBannerHidden);
  const hiddenCallsChipShown = useAppSelector(selectHiddenCallsChipShown);
  const voiceEnabled = Boolean(
    useAppSelector(selectCurrentPractice)?.voice_enabled
  );
  const visibleMenuItems = useVisibleMenuItems();
  const emitFocusPatientSearch = useEventBusEmit(
    QUICK_LAUNCHER_FOCUS_PATIENT_SEARCH
  );
  const { isSwitched, isBreakGlass, activePracticeId, homePracticeId } =
    useActiveSessionContext();
  const { switchToPractice } = useSwitchPractice();

  const isMultiPracticeUser = Boolean(currentUser?.is_multi_practice_user);

  return useMemo(() => {
    const actions: QuickLauncherAction[] = [
      ...contextActions,
      {
        id: "action:new-patient",
        label: "New patient",
        description: "Open the new patient wizard",
        group: "Actions",
        Icon: PersonAddAltOutlinedIcon,
        keywords: ["create", "add", "register", "intake"],
        perform: () => navigate("/patients/new")
      },
      {
        id: "action:new-appointment",
        label: "New appointment",
        description: "Open the appointment form right here",
        group: "Actions",
        Icon: EventAvailableOutlinedIcon,
        keywords: ["book", "schedule", "create", "calendar", "visit"],
        // Opens in place on any page: the launcher hosts its own
        // `AppointmentsProvider` (see `quick-launcher-hosts.component.tsx`), so
        // there is no detour through /scheduling. On a chart the "This patient"
        // section offers the same form with the patient pre-selected.
        perform: () => dispatch(requestNewAppointment())
      },
      ...(hasPatientSearch
        ? [
            {
              id: "action:focus-patient-search",
              label: "Search patients",
              description: "Jump to the search box in the top bar",
              group: "Actions" as const,
              Icon: SearchIcon,
              keywords: ["find", "lookup", "name", "phone", "serial"],
              perform: () => emitFocusPatientSearch()
            }
          ]
        : []),
      {
        id: "action:screenshot",
        // Internal tooling: it exists so Suno staff can illustrate a bug or a
        // support answer without leaving the app. Customer practices have their
        // own OS screenshot tools, and handing every user a one-keystroke way to
        // put a chart on the clipboard is not a trade worth making.
        sunoOnly: true,
        label: "Screenshot this page",
        description: "Frame the page and copy it, or save it as a PNG",
        group: "Actions",
        Icon: PhotoCameraOutlinedIcon,
        keywords: [
          "screenshot",
          "capture",
          "screen grab",
          "snip",
          "image",
          "png",
          "share",
          "bug report"
        ],
        // The capture itself happens in `screenshot/`, a beat after this runs —
        // the launcher has to finish closing first, and the request carries no
        // payload because the subject is whatever is on screen by then.
        perform: () => dispatch(requestScreenshot())
      }
    ];

    visibleMenuItems.forEach(({ to, primary, Icon, tooltip }) => {
      actions.push({
        id: `navigate:${to}`,
        label: primary,
        description: tooltip,
        group: "Navigate",
        Icon,
        keywords: [to.replace(/^\//, "")],
        perform: () => navigate(to)
      });
    });

    // Offered only where it can do something: the banner renders for
    // multi-practice users only, so for anyone else this would be a control with
    // no visible effect. `sunoOnly` on top: the practice switcher is an internal
    // support tool, so even a multi-practice customer user never sees it here.
    if (isMultiPracticeUser) {
      actions.push({
        id: "preference:practice-switcher",
        sunoOnly: true,
        label: practiceBannerHidden
          ? "Show practice switcher"
          : "Hide practice switcher",
        description: practiceBannerHidden
          ? "Bring back the practice context banner at the top of the app"
          : "Hide the practice context banner — this panel still names your practice",
        group: "Preferences",
        Icon: practiceBannerHidden
          ? VisibilityOutlinedIcon
          : VisibilityOffOutlinedIcon,
        keywords: [
          "practice",
          "switcher",
          "banner",
          "admin mode",
          "context",
          "toggle"
        ],
        trailingLabel: practiceBannerHidden ? "Hidden" : "Visible",
        perform: () => dispatch(togglePracticeBannerHidden())
      });
    }

    // The Calls page's "Hidden" chip (ENG-2364) is the one control there that a
    // practice user never has, and Suno accounts are what demos run on, so it
    // stays away until asked for here. Offered only where the chip can exist:
    // the workspace is Suno Voice.
    if (voiceEnabled) {
      actions.push({
        id: "preference:hidden-calls-chip",
        sunoOnly: true,
        label: hiddenCallsChipShown
          ? "Hide the Hidden calls chip"
          : "Show the Hidden calls chip",
        description: hiddenCallsChipShown
          ? "Back to the Calls page as the practice sees it — for demos"
          : "List the calls this practice has hidden, on the Calls page",
        group: "Preferences",
        Icon: hiddenCallsChipShown
          ? VisibilityOffOutlinedIcon
          : VisibilityOutlinedIcon,
        keywords: [
          "hidden calls",
          "private calls",
          "pill",
          "chip",
          "demo",
          "calls",
          "toggle"
        ],
        trailingLabel: hiddenCallsChipShown ? "Visible" : "Hidden",
        perform: () => dispatch(toggleHiddenCallsChipShown())
      });
    }

    // The banner can now be hidden even while switched, and it carries the only
    // Exit button — so the launcher has to be able to end the switch too, or a
    // hidden banner would strand the user in a foreign practice. Mirrors
    // `PracticeContextBanner`'s own handler, including the break-glass case
    // (those sessions cannot switch back; the backend rejects it).
    if (isSwitched) {
      actions.push({
        id: "action:exit-practice-context",
        sunoOnly: true,
        label: isBreakGlass ? "End break-glass session" : "Exit admin mode",
        description: isBreakGlass
          ? "Log out — a break-glass session cannot switch back"
          : "Return to your home practice",
        group: "Actions",
        Icon: LogoutOutlinedIcon,
        keywords: ["exit", "leave", "home practice", "switch back", "stop"],
        perform: () => {
          if (isBreakGlass) {
            if (token) dispatch(logoutUser({ token }));
            return;
          }
          if (homePracticeId != null) {
            switchToPractice(homePracticeId, activePracticeId ?? undefined);
          }
        }
      });
    }

    actions.push({
      id: "preference:shortcuts-help",
      label: "Keyboard shortcuts",
      description: "A short guide to this launcher",
      group: "Preferences",
      Icon: HelpOutlineIcon,
      keywords: ["help", "guide", "how to", "hotkey", "tips"],
      // Same dialog as the account menu's "Keyboard shortcuts" item.
      perform: () => dispatch(openQuickLauncherHelp())
    });

    return isSunoAdmin ? actions : actions.filter((action) => !action.sunoOnly);
  }, [
    dispatch,
    navigate,
    emitFocusPatientSearch,
    contextActions,
    hasPatientSearch,
    isSunoAdmin,
    visibleMenuItems,
    isMultiPracticeUser,
    practiceBannerHidden,
    hiddenCallsChipShown,
    voiceEnabled,
    isSwitched,
    isBreakGlass,
    homePracticeId,
    activePracticeId,
    switchToPractice,
    token
  ]);
}
